// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { BrandProductLinesStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { SectionTitleStyles } from "@mcmc/App/Widgets/SectionTitleWidget/SectionTitleWidget";
import categoryListStyles from "@mcmc/Overrides/Widgets/Category/CategoryList.style";
import StyledBox from "@mcmc/XDL/Components/Common/Box";
import CloudinaryImage from "@mcmc/XDL/Components/Common/CloudinaryImage";
import { Roboto } from "@mcmc/XDL/Utilities";
import { transformationsMap } from "@mcmc/XDL/Utilities/cloudinary";
import mergeToNew from "@mcmc/XDL/Utilities/xMergeToNew";
import React, { FC, useContext, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    showImages = "showImages",
    maxToShow = "maxToShow",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.showImages]: boolean;
        [fields.maxToShow]: number;
    };
}

const mapStateToProps = (state: ApplicationState) => ({});

const mapDispatchToProps = {};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface BrandDetailsProductLinesStyles {
    container?: InjectableCss;
    linkWrapper?: InjectableCss;
    viewAllLink?: LinkPresentationProps;
    titleWrapper?: InjectableCss;
    titleWidgetStyle?: SectionTitleStyles;
}

export const productLinesStyles: BrandDetailsProductLinesStyles = {
    container: {
        css: css`
            padding-bottom: 30px;
        `,
    },
    titleWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            margin: 0 -5px 25px -5px;
        `,
    },
    linkWrapper: {
        css: css`
            position: relative;
            padding: 0 5px;
        `,
    },
    viewAllLink: {
        typographyProps: {
            ...Roboto,
            size: 16,
            lineHeight: "21px",
            weight: 500,
            css: css`
                letter-spacing: 0;
            `,
        },
    },
    titleWidgetStyle: {
        wrapper: {
            css: css`
                margin: 0;
            `,
        },
    },
};

const BrandDetailsProductLines: FC<Props> = ({ fields }) => {
    const [styles] = React.useState(() => mergeToNew(categoryListStyles, productLinesStyles));

    const { isLoading, value: brandProductLines } = useContext(BrandProductLinesStateContext);
    const { title, showImages, maxToShow } = fields;
    const [viewCount, setViewCount] = React.useState(maxToShow);

    useEffect(() => {
        setViewCount(maxToShow);
    }, [maxToShow]);

    if (isLoading || !brandProductLines || brandProductLines!.length === 0) {
        return null;
    }

    const handleShowAllClicked = () => {
        setViewCount(brandProductLines?.length || maxToShow);
    };

    const renderShowAllLink = () => {
        if (maxToShow === 0 || (brandProductLines || []).length <= viewCount) {
            return null;
        }
        return (
            <StyledWrapper {...styles.linkWrapper}>
                <Link
                    {...styles.viewAllLink}
                    onClick={handleShowAllClicked}
                    data-test-selector="brandProductLineShowAllLink"
                    aria-label={translate("Show All Brand Product Lines")}
                >
                    {translate("Show All")}
                </Link>
            </StyledWrapper>
        );
    };

    return (
        <StyledWrapper {...styles.container} data-test-selector="brandProductLines">
            <StyledWrapper {...styles.titleWrapper}>
                {title &&
                    createWidgetElement("SectionTitleWidget/SectionTitleWidget", {
                        fields: {
                            title,
                        },
                        extendedStyles: styles.titleWidgetStyle,
                    } as any)}
                {renderShowAllLink()}
            </StyledWrapper>
            <StyledWrapper {...styles.categoriesContentWrapper}>
                {brandProductLines?.slice(0, viewCount).map(productLine => (
                    <StyledBox {...styles.styledBox} key={productLine.id.toString()}>
                        <Link
                            href={productLine.productListPagePath}
                            {...styles.link}
                            aria-label={productLine.featuredImageAltText}
                        >
                            {showImages && (
                                <StyledWrapper {...styles.categoryImageWrapper}>
                                    <CloudinaryImage
                                        imageTransformations={transformationsMap.product_sm}
                                        src={productLine.featuredImagePath}
                                        altText={productLine.featuredImageAltText}
                                        data-test-selector={`brandProductLineImage_${productLine.id}`}
                                    />
                                </StyledWrapper>
                            )}
                            <StyledWrapper {...styles.categoryNameWrapper}>
                                <Typography {...styles.categoryName}>{productLine.name}</Typography>
                            </StyledWrapper>
                        </Link>
                    </StyledBox>
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(BrandDetailsProductLines),
    definition: {
        group: "Brand Details",
        displayName: "Brand Product Lines",
        allowedContexts: ["BrandDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Product Lines",
                fieldType: "Translatable",
            },
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.maxToShow,
                displayName: "Max To Show",
                editorTemplate: "IntegerField",
                defaultValue: 8,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
